interface ServiceContent {
    symptoms: string[];
    treatments: string[];
    preventiveMeasures: string[];
    riskFactors: string[];
    diagnosis: string[];
}

export interface Service {
    id: string;
    title: string;
    description: string;
    imageUrl: string;
    content: ServiceContent;
}

export const services: Service[] = [
    {
        id: "lap-cholecystectomy",
        title: "Laparoscopic Cholecystectomy",
        description: "A minimally invasive surgical procedure to remove the gallbladder using small incisions and special instruments.",
        imageUrl: "/images/lap-cholecystectomy.png",
        content: {
            symptoms: [
                "Severe abdominal pain",
                "Nausea and vomiting",
                "Pain after eating",
                "Fever",
                "Yellowing of skin",
                "Clay-colored stools",
                "Indigestion"
            ],
            treatments: [
                "Laparoscopic gallbladder removal",
                "Pain management",
                "Dietary modifications",
                "Post-operative care",
                "Recovery monitoring"
            ],
            preventiveMeasures: [
                "Maintaining healthy weight",
                "Balanced diet",
                "Regular exercise",
                "Avoiding fatty foods",
                "Staying hydrated"
            ],
            riskFactors: [
                "Obesity",
                "Age over 40",
                "Female gender",
                "Rapid weight loss",
                "Family history",
                "Diabetes"
            ],
            diagnosis: [
                "Ultrasound",
                "CT scan",
                "HIDA scan",
                "Blood tests",
                "Physical examination"
            ]
        }
    },
    {
        id: "lap-hernia-repair",
        title: "Laparoscopic Hernia Repair",
        description: "Minimally invasive surgery to repair hernias using small incisions and mesh reinforcement.",
        imageUrl: "/images/lap-hernia.jpg",
        content: {
            symptoms: [
                "Visible bulge or lump",
                "Pain or discomfort",
                "Pressure sensation",
                "Pain when lifting",
                "Increasing pain throughout day",
                "Nausea and vomiting if strangulated"
            ],
            treatments: [
                "Laparoscopic mesh repair",
                "Pain management",
                "Activity modification",
                "Physical therapy",
                "Post-operative monitoring"
            ],
            preventiveMeasures: [
                "Maintaining healthy weight",
                "Proper lifting techniques",
                "Core strengthening exercises",
                "Avoiding heavy lifting",
                "Quitting smoking"
            ],
            riskFactors: [
                "Obesity",
                "Chronic cough",
                "Previous surgery",
                "Family history",
                "Pregnancy",
                "Straining"
            ],
            diagnosis: [
                "Physical examination",
                "Ultrasound",
                "CT scan",
                "MRI",
                "Clinical assessment"
            ]
        }
    },
    // Add this new service to the services array
{
    id: "lap-robotic-gi-hpb",
    title: "Laparoscopic & Robotic GI/HPB Surgery",
    description: "Advanced minimally invasive surgical procedures for gastrointestinal and hepato-pancreato-biliary conditions using laparoscopic and robotic technology.",
    imageUrl: "/images/robo.jpg",
    content: {
        symptoms: [
            "Abdominal pain or discomfort",
            "Changes in bowel habits",
            "Unexplained weight loss",
            "Jaundice",
            "Early satiety",
            "Difficulty swallowing",
            "Gastrointestinal bleeding",
            "Nausea and vomiting",
            "Loss of appetite"
        ],
        treatments: [
            "Robotic-assisted gastrectomy",
            "Laparoscopic liver resection",
            "Robotic pancreatic surgery",
            "Minimally invasive bowel resection",
            "Robot-assisted biliary procedures",
            "Laparoscopic lymph node dissection",
            "Advanced imaging-guided surgery"
        ],
        preventiveMeasures: [
            "Regular health screenings",
            "Maintaining healthy weight",
            "Balanced nutrition",
            "Regular exercise",
            "Smoking cessation",
            "Limited alcohol consumption",
            "Regular medical check-ups"
        ],
        riskFactors: [
            "Previous abdominal surgery",
            "Obesity",
            "Advanced age",
            "Chronic diseases",
            "Family history of GI cancers",
            "Smoking history",
            "Chronic inflammation"
        ],
        diagnosis: [
            "3D CT imaging",
            "MRI scanning",
            "Diagnostic laparoscopy",
            "Endoscopic evaluation",
            "PET-CT scanning",
            "Blood marker analysis",
            "Real-time fluorescence imaging"
        ]
    }
},
    {
        id: "bile-duct-cancer",
        title: "Bile Duct Cancer Surgery",
        description: "Surgical procedures to remove cancerous tissue from the bile ducts, which carry bile from the liver to the small intestine.",
        imageUrl: "/images/bile-ducts-cancer.webp",
        content: {
            symptoms: [
                "Jaundice",
                "Itchy skin",
                "Abdominal pain",
                "Weight loss",
                "Dark urine",
                "Light-colored stools",
                "Fever"
            ],
            treatments: [
                "Surgical resection",
                "Bile duct reconstruction",
                "Chemotherapy",
                "Radiation therapy",
                "Palliative procedures"
            ],
            preventiveMeasures: [
                "Regular check-ups",
                "Liver health monitoring",
                "Avoiding risk factors",
                "Healthy lifestyle",
                "Early detection screening"
            ],
            riskFactors: [
                "Primary sclerosing cholangitis",
                "Age over 60",
                "Chronic liver disease",
                "Bile duct stones",
                "Parasitic infections"
            ],
            diagnosis: [
                "Blood tests",
                "Imaging studies",
                "ERCP",
                "Biopsy",
                "Tumor markers"
            ]
        }
    },
    {
        id: "pancreatic-surgery",
        title: "Pancreatic Surgery",
        description: "Complex surgical procedures to treat various pancreatic conditions including cancer, pancreatitis, and other disorders.",
        imageUrl: "/images/pancreatic-surgery.jpg",
        content: {
            symptoms: [
                "Abdominal pain",
                "Back pain",
                "Weight loss",
                "Loss of appetite",
                "Diabetes",
                "Jaundice",
                "Nausea"
            ],
            treatments: [
                "Whipple procedure",
                "Distal pancreatectomy",
                "Total pancreatectomy",
                "Minimally invasive surgery",
                "Pain management"
            ],
            preventiveMeasures: [
                "Healthy diet",
                "Regular exercise",
                "Avoiding alcohol",
                "Smoking cessation",
                "Regular check-ups"
            ],
            riskFactors: [
                "Smoking",
                "Chronic pancreatitis",
                "Diabetes",
                "Family history",
                "Age over 65",
                "Obesity"
            ],
            diagnosis: [
                "CT scan",
                "MRI",
                "Endoscopic ultrasound",
                "Blood tests",
                "Biopsy"
            ]
        }
    },
    {
        id: "gastro-oncosurgery",
        title: "Gastro-Oncosurgery",
        description: "Specialized surgical procedures for treating various types of gastrointestinal cancers.",
        imageUrl: "/images/gastro-onco.jpg",
        content: {
            symptoms: [
                "Unexplained weight loss",
                "Abdominal pain",
                "Changes in bowel habits",
                "Bleeding",
                "Loss of appetite",
                "Early satiety",
                "Fatigue"
            ],
            treatments: [
                "Tumor resection",
                "Lymph node removal",
                "Reconstruction",
                "Chemotherapy",
                "Radiation therapy"
            ],
            preventiveMeasures: [
                "Regular screening",
                "Healthy diet",
                "Regular exercise",
                "Smoking cessation",
                "Limited alcohol consumption"
            ],
            riskFactors: [
                "Age",
                "Family history",
                "Poor diet",
                "Smoking",
                "H. pylori infection",
                "Chronic inflammation"
            ],
            diagnosis: [
                "Endoscopy",
                "Biopsy",
                "CT scan",
                "PET scan",
                "Blood tests"
            ]
        }
    },
    {
        id: "liver-surgery",
        title: "Liver Surgery",
        description: "Surgical procedures to treat various liver conditions including tumors, cysts, and other liver diseases.",
        imageUrl: "/images/liver-surgery.jpg",
        content: {
            symptoms: [
                "Abdominal pain",
                "Jaundice",
                "Fatigue",
                "Weight loss",
                "Nausea",
                "Enlarged liver",
                "Easy bruising"
            ],
            treatments: [
                "Liver resection",
                "Tumor ablation",
                "Portal vein embolization",
                "Minimally invasive surgery",
                "Reconstruction"
            ],
            preventiveMeasures: [
                "Regular check-ups",
                "Healthy diet",
                "Limited alcohol use",
                "Hepatitis prevention",
                "Weight management"
            ],
            riskFactors: [
                "Cirrhosis",
                "Hepatitis",
                "Alcohol abuse",
                "Fatty liver disease",
                "Family history"
            ],
            diagnosis: [
                "Liver function tests",
                "Imaging studies",
                "Biopsy",
                "Blood tests",
                "Genetic testing"
            ]
        }
    },
    {
        id: "intestinal-surgery",
        title: "Intestinal Surgery",
        description: "Surgical procedures to treat various conditions affecting the small and large intestines.",
        imageUrl: "/images/intestinal-surgery.jpeg",
        content: {
            symptoms: [
                "Abdominal pain",
                "Changes in bowel habits",
                "Bleeding",
                "Bloating",
                "Weight loss",
                "Fatigue",
                "Nausea"
            ],
            treatments: [
                "Bowel resection",
                "Strictureplasty",
                "Colectomy",
                "Minimally invasive surgery",
                "Stoma creation"
            ],
            preventiveMeasures: [
                "Healthy diet",
                "Regular exercise",
                "Adequate hydration",
                "Regular screening",
                "Stress management"
            ],
            riskFactors: [
                "Inflammatory bowel disease",
                "Family history",
                "Age",
                "Poor diet",
                "Smoking"
            ],
            diagnosis: [
                "Colonoscopy",
                "Endoscopy",
                "CT scan",
                "MRI",
                "Blood tests"
            ]
        }
    },
    {
        id: "colorectal-oncosurgery",
        title: "Colorectal Oncosurgery",
        description: "Specialized surgical procedures for treating cancers of the colon and rectum.",
        imageUrl: "/images/colorectal-onco.jpg",
        content: {
            symptoms: [
                "Change in bowel habits",
                "Rectal bleeding",
                "Abdominal pain",
                "Weight loss",
                "Fatigue",
                "Anemia",
                "Persistent constipation"
            ],
            treatments: [
                "Tumor resection",
                "Colostomy",
                "Lymph node removal",
                "Minimally invasive surgery",
                "Combined therapy"
            ],
            preventiveMeasures: [
                "Regular screening",
                "High-fiber diet",
                "Regular exercise",
                "Weight management",
                "Limited red meat consumption"
            ],
            riskFactors: [
                "Age over 50",
                "Family history",
                "Inflammatory bowel disease",
                "Poor diet",
                "Obesity",
                "Smoking"
            ],
            diagnosis: [
                "Colonoscopy",
                "Biopsy",
                "CT scan",
                "MRI",
                "Blood tests"
            ]
        }
    },
    {
        id: "liver-transplantation",
        title: "Liver Transplantation",
        description: "Surgical replacement of a diseased liver with a healthy donor liver, either from a deceased or living donor.",
        imageUrl: "/images/liver-transplant.jpg",
        content: {
            symptoms: [
                "End-stage liver disease",
                "Jaundice",
                "Ascites",
                "Mental confusion",
                "Bleeding problems",
                "Fatigue",
                "Portal hypertension"
            ],
            treatments: [
                "Liver transplant surgery",
                "Immunosuppression therapy",
                "Post-transplant care",
                "Rejection monitoring",
                "Rehabilitation"
            ],
            preventiveMeasures: [
                "Medication compliance",
                "Regular check-ups",
                "Infection prevention",
                "Healthy lifestyle",
                "Avoiding alcohol"
            ],
            riskFactors: [
                "Severe liver disease",
                "Liver cancer",
                "Acute liver failure",
                "Metabolic diseases",
                "Bile duct diseases"
            ],
            diagnosis: [
                "Liver function tests",
                "Imaging studies",
                "Blood type matching",
                "Psychological evaluation",
                "Pre-transplant workup"
            ]
        }
    },
    {
        id: "pancreas-transplantation",
        title: "Pancreas Transplantation",
        description: "Surgical procedure to place a healthy donor pancreas into a patient whose pancreas no longer functions properly.",
        imageUrl: "/images/pancreas-transplant.jpeg",
        content: {
            symptoms: [
                "Type 1 diabetes",
                "Kidney failure",
                "Poor glucose control",
                "Frequent hypoglycemia",
                "Diabetic complications",
                "Organ damage",
                "Quality of life impact"
            ],
            treatments: [
                "Pancreas transplant surgery",
                "Immunosuppression therapy",
                "Blood sugar monitoring",
                "Post-transplant care",
                "Rehabilitation"
            ],
            preventiveMeasures: [
                "Strict medication adherence",
                "Regular monitoring",
                "Infection prevention",
                "Healthy lifestyle",
                "Regular exercise"
            ],
            riskFactors: [
                "Type 1 diabetes",
                "End-stage renal disease",
                "Cardiovascular disease",
                "Age",
                "Overall health status"
            ],
            diagnosis: [
                "Blood tests",
                "Tissue typing",
                "Cardiac evaluation",
                "Psychological evaluation",
                "Pre-transplant screening"
            ]
        }
    },
    {
        id: "intestinal-multivisceral-transplantation",
        title: "Intestinal and Multivisceral Transplantation",
        description: "Complex surgical procedure involving the transplantation of the small bowel alone or in combination with other abdominal organs.",
        imageUrl: "/images/intestinal-transplant.jpg",
        content: {
            symptoms: [
                "Intestinal failure",
                "Malnutrition",
                "TPN dependence",
                "Liver dysfunction",
                "Abdominal pain",
                "Weight loss",
                "Frequent infections"
            ],
            treatments: [
                "Intestinal transplantation",
                "Multivisceral transplantation",
                "Immunosuppression therapy",
                "Nutritional support",
                "Rejection monitoring"
            ],
            preventiveMeasures: [
                "Infection prevention",
                "Strict medication compliance",
                "Regular monitoring",
                "Nutritional management",
                "Lifestyle modifications"
            ],
            riskFactors: [
                "Short bowel syndrome",
                "TPN complications",
                "Previous surgeries",
                "Abdominal trauma",
                "Intestinal disorders"
            ],
            diagnosis: [
                "Nutritional assessment",
                "Blood tests",
                "Imaging studies",
                "Tissue typing",
                "Pre-transplant evaluation"
            ]
        }
    }
];